import injector from 'vue-inject'
import utils from '../utils'
import axiosCancel from 'axios-cancel'

/**
 * Service use to retrieve charts and sessions data
 */
class DataService {
  constructor(axios, apiRoot) {
    this.axios = axios
    this.apiRoot = apiRoot
    //this.queue = housecall({concurrency: 5, cooldown: 1000});
    axiosCancel(this.axios, {
      debug: false,
    })
    //this.manager = ConcurrencyManager(this.axios, MAX_CONCURRENT_REQUESTS);
  }

  /**
   * Get all users
   */
  async getUsers() {
    const url = `${this.apiRoot}/users`
    return this.axios.get(url).then(response => response.data)
  }

  /**
   * Get all comments according to selected filters
   * @param {*} config
   */
  async getComments(config) {
    if (config.count && config.count > 0) {
      const url = `${this.apiRoot}/comments?page=${config.currentPage}&count=${config.count}&site_location=${config.sites}&language=${config.language}&histopad_rating=${config.histopad_rating}&visit_rating=${config.visit_rating}&order=${config.order}&list=${config.bookmarkList}&user_id=${config.userId}&keyword=${config.keyword}&startDate=${config.date.start}&endDate=${config.date.end}`
      return this.axios.get(url).then(response => response.data)
    } else {
      return {
        result: {
          comments: [],
        },
      }
    }
  }

  /**
   * Get bookmarklist comments
   * @param {*} bookmarkList
   */
  async getBookmarkListComments(bookmarkList) {
    const url = `${this.apiRoot}/comments/bookmark/list/` + bookmarkList
    return this.axios.get(url).then(response => response.data)
  }

  /**
   * Delete a specific bookmark
   * @param {*} bookmarkList
   * @param {*} session
   */
  async deleteBookmark(session, bookmarkList) {
    const url = `${this.apiRoot}/comments/bookmark/delete`
    var form = {
      session_id: session,
      list: bookmarkList,
    }
    return this.axios
      .delete(url, {
        data: form,
      })
      .then(response => response.data)
  }

  /**
   * Delete bookmark lists
   * @param {*} ids
   */
  async deleteBookmarkLists(ids) {
    const url = `${this.apiRoot}/comments/bookmarklist/delete`
    let form = {
      lists: ids,
    }
    return this.axios
      .delete(url, {
        data: form,
      })
      .then(response => response.data)
  }

  /**
   * Download comments in XLS format
   * @param {*} commentForm
   * @param {*} sites
   * @param {*} userId
   * @param {*} start
   * @param {*} end
   */
  async downloadComments(commentForm, sites, email, start, end, language) {
    const url = `${this.apiRoot}/comments/download?site_location=${sites}&language=${commentForm.language}&histopad_rating=${commentForm.histopad_rating}&visit_rating=${commentForm.visit_rating}&order=${commentForm.order}&score_min=${commentForm.score_min}&score_max=${commentForm.score_max}&email=${email}&list=${commentForm.bookmarkList}&keyword=${commentForm.keyword}&startDate=${start}&endDate=${end}&lang=${language}`
    return this.axios
      .get(url, {
        responseType: 'blob',
      })
      .then(response => response.data)
  }

  /**
   * Download logs according to selected filters and period
   * @param {*} logForm
   * @param {*} start
   * @param {*} end
   */
  async downloadLogs(logForm, totalCount, email, start, end) {
    let url = `${this.apiRoot}/device/logs/download?startDate=${start}&endDate=${end}&totalCount=${totalCount}&email=${email}`
    for (let key in logForm) {
      if (logForm[key]) {
        url += `&${key}=${logForm[key]}`
      }
    }
    return this.axios
      .get(url, {
        responseType: 'blob',
      })
      .then(response => response.data)
  }

  /**
   * Get all users linked to a specific site ID
   * @param {*} commentId
   */
  async getUsersForSite(siteId) {
    const url = `${this.apiRoot}/users?site_id=${siteId}`
    return this.axios.get(url).then(response => response.data)
  }

  /**
   * Get bookmark lists of an user
   * @param {*} userId
   */
  async getUserBookmarkLists(userId, type = 'manual') {
    const url = `${this.apiRoot}/comments/bookmark/list/user?user_id=${userId}&type=${type}`
    return this.axios.get(url).then(response => response.data)
  }

  /**
   * Get bookmark lists linked to a session's comment and an user
   * @param {*} userId
   */
  async getUserBookmarkListsForComment(userId, commentId) {
    const url = `${this.apiRoot}/comments/bookmark/list?user_id=${userId}&id_session=${commentId}`
    return this.axios.get(url).then(response => response.data)
  }

  /**
   * Add a comment into a bookmarks list
   * @param {*} userId
   * @param {*} commentId
   * @param {*} bookmarkLists
   */
  async addCommentToBookmarkLists(userId, commentId, bookmarkLists) {
    const url = `${this.apiRoot}/comments/bookmark/list/add/`
    var form = {
      id_session: commentId,
      bookmark_lists: bookmarkLists,
      user_id: userId,
    }
    return this.axios.post(url, form).then(response => response.data)
  }

  /**
   * Create a config
   * @param {*} name
   * @param {*} display
   * @param {*} query
   * @param {*} userId
   * @param {*} bookmarkId
   * @param {*} queryType
   */
  async createStreamProfile(name, display, query, userId, bookmarkId, queryType) {
    const url = `${this.apiRoot}/comments/stream/config/create`

    return this.axios.post(url, {
      user_id: userId,
      query: query,
      display: display,
      bookmarkId: bookmarkId,
      name: name,
      queryType: queryType,
    })
  }

  async deleteStreamProfiles(id) {
    const url = `${this.apiRoot}/comments/stream/config/delete`
    return this.axios.delete(url, { data: { id: id } })
  }

  /**
   * Create user stream profiles
   * @param {*} userId
   */
  async getUserStreamProfiles(userId) {
    const url = `${this.apiRoot}/comments/stream/configs?user=${userId}`
    return this.axios.get(url).then(response => response.data)
  }

  /**
   * Update stream profile
   * @param {*} name
   * @param {*} query
   * @param {*} display
   * @param {*} id
   * @param {*} bookmarkListId
   * @param {*} queryType
   */
  async updateStreamProfile(name, display, query, id, bookmarkListId, queryType) {
    const url = `${this.apiRoot}/comments/stream/config/update`

    return this.axios
      .post(url, {
        id: id,
        query: query,
        display: display,
        name: name,
        bookmarkListId: bookmarkListId,
        queryType: queryType,
      })
      .then(response => response.data)
  }

  /**
   * Get stream profil
   * @param {*} id
   */
  async getStreamProfile(id) {
    const url = `${this.apiRoot}/comments/stream/config?id=${id}`
    return this.axios.get(url).then(response => response.data)
  }

  /**
   * Create a new bookmark list for a specific user
   * @param {*} userId
   * @param {*} title
   * @param {*} commentId
   */
  async createBookmarkList(userId, title, commentId, type = 'manual', config) {
    const url = `${this.apiRoot}/comments/bookmark/list/create`
    var form = {
      user_id: userId,
      id_session: commentId,
      title: title,
      type: type,
      config: config,
    }
    return this.axios.post(url, form).then(response => response.data)
  }

  /**
   *
   * @param {*} userId
   * @param {*} title
   * @param {*} commentId
   */
  async createBookmarkListAuto(userId, title, config) {
    const url = `${this.apiRoot}/comments/bookmark/list/create/auto`
    let form = {
      user_id: userId,
      title: title,
      config: config,
    }
    return this.axios.post(url, form).then(response => response.data)
  }

  /**
   * Share a comment to users
   * @param {*} commentId
   * @param {*} users
   */
  async shareComment(commentId, users) {
    const url = `${this.apiRoot}/comments/bookmark/share`
    const form = {
      user_ids: users,
      id_session: commentId,
    }
    return this.axios.post(url, form).then(response => response.data)
  }

  /**
   * Return all routes over period according to selected site
   * @param {*} filtersValues
   * @param {*} start
   * @param {*} end
   */
  async getRoutes(formControl, filtersValues, start, end, previousRequestId) {
    return new Promise((resolve, reject) => {
      let path =
        `/route?site_location=` +
        formControl.site +
        '&dataType=' +
        formControl.dataType +
        '&startDate=' +
        start +
        '&endDate=' +
        end
      for (let key in filtersValues) {
        if (filtersValues[key] != 'all') {
          path += '&' + key + '=' + filtersValues[key]
        }
      }
      const url = `${this.apiRoot}${path}`

      this.cancelDataRequest(previousRequestId)
        .then(() => {
          return this.axios.get(url, {
            requestId: `route${formControl.site}${formControl.dataType}${start}${end}`,
          })
        })
        .then(response => {
          resolve(response.data)
        })
        .catch(error => {
          if (this.axios.isCancel(error)) {
            reject(Error('cancel'))
          } else {
            reject(error)
          }
        })
    })
  }

  /**
   * Get GPX coordinates
   * @param {*} previousRequestId
   */
  async getGPXCoordinates(previousRequestId) {
    return new Promise((resolve, reject) => {
      const url = `${this.apiRoot}/route/gpx`

      /*for (let key in form) {
                                      if (key != 'site' && form[key] != 'all' && form[key]) {
                                        url += `&${key}=${form[key]}`
                                      }
                                    }*/

      this.cancelDataRequest(previousRequestId)
        .then(() => {
          return this.axios.get(
            url,
            /*, {
                                                                                   requestId: `routesGPX${JSON.stringify(form)}${start}${end}`,
                                                                                 }*/
          )
        })
        .then(response => {
          resolve(response.data)
        })
        .catch(error => {
          if (this.axios.isCancel(error)) {
            reject(Error('cancel'))
          } else {
            reject(error)
          }
        })
    })
  }

  /**
   * Get GPX coordinates
   * @param {*} previousRequestId
   */
  async getGeoJSONCoordinates(previousRequestId) {
    return new Promise((resolve, reject) => {
      const url = `${this.apiRoot}/route/geojson`

      /*for (let key in form) {
                                      if (key != 'site' && form[key] != 'all' && form[key]) {
                                        url += `&${key}=${form[key]}`
                                      }
                                    }*/

      this.cancelDataRequest(previousRequestId)
        .then(() => {
          return this.axios.get(
            url,
            /*, {
                                                                                   requestId: `routesGPX${JSON.stringify(form)}${start}${end}`,
                                                                                 }*/
          )
        })
        .then(response => {
          resolve(response.data)
        })
        .catch(error => {
          if (this.axios.isCancel(error)) {
            reject(Error('cancel'))
          } else {
            reject(error)
          }
        })
    })
  }

  /**
   *
   * @param {*} commentForm
   * @param {*} sites
   * @param {*} userId
   * @param {*} start
   * @param {*} end
   */
  async downloadMapData(formControl, filtersValues, start, end, lang) {
    let path =
      `/route/download?site_location=` +
      formControl.site +
      '&dataType=' +
      formControl.dataType +
      '&startDate=' +
      start +
      '&endDate=' +
      end +
      '&lang=' +
      lang

    for (let key in filtersValues) {
      if (filtersValues[key] != 'all') {
        path += '&' + key + '=' + filtersValues[key]
      }
    }

    const url = `${this.apiRoot}${path}`
    return this.axios
      .get(url, {
        responseType: 'blob',
      })
      .then(response => response.data)
  }

  /**
   * Get survey answers of a specific site over period
   * @param {*} site
   * @param {*} start
   * @param {*} end
   */
  async getSurvey(form, start, end, previousRequestId) {
    return new Promise((resolve, reject) => {
      let url = `${this.apiRoot}/survey?startDate=${start}&endDate=${end}&site_location=${form.site}`

      for (let key in form) {
        if (key != 'site' && form[key] != 'all' && form[key]) {
          url += `&${key}=${form[key]}`
        }
      }

      this.cancelDataRequest(previousRequestId)
        .then(() => {
          return this.axios.get(url, {
            requestId: `survey${JSON.stringify(form)}${start}${end}`,
          })
        })
        .then(response => {
          resolve(response.data)
        })
        .catch(error => {
          if (this.axios.isCancel(error)) {
            reject(Error('cancel'))
          } else {
            reject(error)
          }
        })
    })
  }

  /**
   * Download survey according to selected site, period and user's language
   * @param {*} site
   * @param {*} start
   * @param {*} end
   * @param {*} lang
   */
  async downloadSurvey(form, start, end, lang, email, totalCount, maxQuestionIndex) {
    let url = `${this.apiRoot}/survey/download?startDate=${start}&endDate=${end}&site_location=${form.site}&lang=${lang}&email=${email}&totalCount=${totalCount}&maxQuestionIndex=${maxQuestionIndex}`

    for (let key in form) {
      if (key != 'site' && form[key] != 'all' && form[key]) {
        url += `&${key}=${form[key]}`
      }
    }

    return this.axios
      .get(url, {
        responseType: 'blob',
      })
      .then(response => response.data)
  }

  /**
   * Download file (comments / emails) over period
   * @param {*} downloadForm
   * @param {*} start
   * @param {*} end
   */
  async downloadFile(downloadForm, start, end) {
    const url = `${this.apiRoot}/download?type=${downloadForm.fileType}&startDate=${start}&endDate=${end}&site_location=${downloadForm.sites}&histopad_rating=${downloadForm.ratings}`
    return this.axios
      .get(url, {
        responseType: 'blob',
      })
      .then(response => response.data)
  }

  /**
   * Get chart data according to his settings over period
   * @param {*} config
   * @param {*} period
   * @param {*} dateFormat
   * @param {*} user
   * @param {*} previousPath
   * @param {*} area
   */
  async getChartData(config, period, dateFormat, user, previousPath, area = undefined) {
    return new Promise((resolve, reject) => {
      var path = utils.pathGenerator(config, period[0], period[1], dateFormat, user, area)
      if (config.distribution_type) {
        path += '&distributionType=' + config.distribution_type.toLowerCase()
      }

      const url = `${this.apiRoot}/${path}`

      //cancel previous request
      this.axios.cancel(previousPath)

      this.cancelDataRequest(previousPath)
        .then(() => {
          return this.axios.get(url, {
            requestId: path + `&chart_id=${config.id}`,
          })
        })
        .then(response => {
          resolve(response.data)
        })
        .catch(error => {
          if (this.axios.isCancel(error)) {
            reject(Error('cancel'))
          } else {
            reject(error)
          }
        })
    })
  }

  /**
   *
   * @param {*} requestId
   */
  async cancelDataRequest(requestId) {
    return Promise.resolve(this.axios.cancel(requestId))
  }

  /**
   * Get support informations for a searched email address
   * @param {*} search
   */
  async getSupportForEmail(search) {
    // const url = `${this.apiRoot}/support/email?email=${search}`
    const url = `${this.apiRoot}/support/email/${search}`
    return this.axios.get(url).then(response => response.data)
  }

  async resendEmailAfterVisit(email, sessionIDs) {
    const url = `${this.apiRoot}/support/resend`
    const payload = {
      sessionIDs: sessionIDs,
      email: email,
    }
    return this.axios.post(url, payload).then(response => response.data)
  }

  /**
   * Get session's timeline informations according to his session ID
   * @param {*} sessionID
   */
  async getSessionTimeline(sessionID) {
    const url = `${this.apiRoot}/session/timeline?id=${sessionID}`
    return this.axios.get(url).then(response => response.data)
  }

  /**
   * Get sessions of a device serial over period
   * @param {*} deviceSerial
   * @param {*} order
   */
  async getDeviceSessions(formControl, start, end, previousRequestId) {
    return new Promise((resolve, reject) => {
      let filters = ``
      for (let key in formControl) {
        if (formControl[key]) {
          filters += `&${key}=${formControl[key]}`
        }
      }

      const url = `${this.apiRoot}/device/informations?startDate=${start}&endDate=${end}${filters}`

      this.cancelDataRequest(previousRequestId)
        .then(() => {
          return this.axios.get(url).then(response => resolve(response.data))
        })
        .catch(error => {
          if (this.axios.isCancel(error)) {
            reject(Error('cancel'))
          } else {
            reject(error)
          }
        })
    })
  }

  /**
   * Get contents best-of for a specific site over period
   * @param {*} siteID
   * @param {*} start
   * @param {*} end
   * @param {*} download
   */
  async getContentBestOf(siteID, start, end, logVersion, download) {
    const url = `${
      this.apiRoot
    }/content/best-of?site_id=${siteID}&logVersion=${logVersion}&startDate=${start}&endDate=${end}&download=${
      download ? 1 : 0
    }`
    return this.axios.get(url).then(response => response.data)
  }
}

injector.service('dataService', ['axios', 'apiRoot'], DataService)
